import React from "react";
import dayjs from "dayjs";
import "./css/NuestroTiempo.css";
import { motion, easeOut, Variants } from "framer-motion";
import "dayjs/locale/es";

const NuestroTiempo: React.FC = () => {
    dayjs.locale("es");
    const [activeCard, setActiveCard] = React.useState<string | null>(null);

    const toggleCard = (id: string) => {
        setActiveCard((prev) => (prev === id ? null : id));
    };

    // =========================
    // FECHAS IMPORTANTES
    // =========================
    const FECHA_JUNTOS = dayjs("2024-05-12");
    const FECHA_VIVIENDO_JUNTOS = dayjs("2024-10-26");
    const hoy = dayjs();

    // =========================
    // CÁLCULOS TIEMPO JUNTOS
    // =========================
    const diasJuntos = hoy.diff(FECHA_JUNTOS, "day");
    const mesesJuntos = hoy.diff(FECHA_JUNTOS, "month");

    const años = hoy.diff(FECHA_JUNTOS, "year");
    const mesesRestantes =
        hoy.subtract(años, "year").diff(FECHA_JUNTOS, "month") % 12;

    // =========================
    // CÁLCULOS VIVIENDO JUNTOS
    // =========================
    const diasViviendo = hoy.diff(FECHA_VIVIENDO_JUNTOS, "day");
    const mesesViviendo = hoy.diff(FECHA_VIVIENDO_JUNTOS, "month");

    // =========================
    // PRÓXIMO ANIVERSARIO
    // =========================
    const calcularProximoAniversario = (fechaInicio: dayjs.Dayjs) => {
        const hoy = dayjs();

        let proximo = fechaInicio.year(hoy.year());

        if (proximo.isBefore(hoy, "day")) {
            proximo = proximo.add(1, "year");
        }

        return {
            fecha: proximo,
            diasRestantes: proximo.diff(hoy, "day"),
        };
    };

    const proximoAniversario = calcularProximoAniversario(FECHA_JUNTOS);

    const calcularNumeroAniversario = (fechaInicio: dayjs.Dayjs) => {
        const hoy = dayjs();
        let aniversarioEsteAño = fechaInicio.year(hoy.year());

        let numero = hoy.year() - fechaInicio.year();

        if (aniversarioEsteAño.isBefore(hoy, "day")) {
            numero += 1;
        }

        return numero;
    };

    const numeroAniversario = calcularNumeroAniversario(FECHA_JUNTOS);

    const fadeUp: Variants = {
        hidden: {
            opacity: 0,
            y: 12,
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: easeOut,
            },
        },
    };

    return (
        <div className="container-fluid">
            <div className="text-center pb-3">
                <h2>Nuestro tiempo</h2>
                <p>Todo lo que hemos construido juntos</p>
            </div>

            <div className="container-fluid content pb-3">
                <div className="contador-page">
                    {/* TIEMPO JUNTOS - PRIORIDAD */}
                    <motion.section
                        className="tiempo-principal"
                        initial="hidden"
                        animate="visible"
                        variants={fadeUp}
                    >
                        <h2>❤️ Tiempo juntos</h2>

                        <div className="stats-grid">
                            <motion.div
                                className={`stat-card ${
                                    activeCard === "dias"
                                        ? "active"
                                        : activeCard
                                        ? "inactive"
                                        : ""
                                }`}
                                onClick={() => toggleCard("dias")}
                                whileTap={{ scale: 0.97 }}
                                transition={{ duration: 0.25, ease: easeOut }}
                            >
                                <span className="stat-number">
                                    {diasJuntos}
                                </span>
                                <span className="stat-label">Días</span>
                            </motion.div>

                            <motion.div
                                className={`stat-card ${
                                    activeCard === "meses"
                                        ? "active"
                                        : activeCard
                                        ? "inactive"
                                        : ""
                                }`}
                                onClick={() => toggleCard("meses")}
                                whileTap={{ scale: 0.97 }}
                                transition={{ duration: 0.25, ease: easeOut }}
                            >
                                <span className="stat-number">
                                    {mesesJuntos}
                                </span>
                                <span className="stat-label">Meses</span>
                            </motion.div>

                            <motion.div
                                className={`stat-card ${
                                    activeCard === "anios"
                                        ? "active"
                                        : activeCard
                                        ? "inactive"
                                        : ""
                                }`}
                                onClick={() => toggleCard("anios")}
                                whileTap={{ scale: 0.97 }}
                                transition={{ duration: 0.25, ease: easeOut }}
                            >
                                <span className="stat-number">
                                    {años}y {mesesRestantes}m
                                </span>
                                <span className="stat-label">Años juntos</span>
                            </motion.div>
                        </div>
                        <motion.div
                            className="aniversario-card"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                delay: 0.3,
                                duration: 0.5,
                                ease: easeOut,
                            }}
                        >
                            <span className="aniversario-emoji">🎉</span>
                            <div>
                                <strong>
                                    Próximo {numeroAniversario}º aniversario en{" "}
                                    {proximoAniversario.diasRestantes} días
                                </strong>

                                <p>
                                    {proximoAniversario.fecha.format(
                                        "D [de] MMMM"
                                    )}
                                </p>
                            </div>
                        </motion.div>
                    </motion.section>

                    {/* SEPARADOR */}
                    <div className="divider" />

                    {/* VIVIENDO JUNTOS - TABLA */}
                    <motion.section
                        className="tiempo-secundario"
                        initial="hidden"
                        animate="visible"
                        variants={fadeUp}
                    >
                        <h2>🏡 Viviendo juntos</h2>

                        <table className="tiempo-table">
                            <thead>
                                <tr>
                                    <th>Días</th>
                                    <th>Meses</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{diasViviendo}</td>
                                    <td>{mesesViviendo}</td>
                                </tr>
                            </tbody>
                        </table>
                    </motion.section>
                </div>
            </div>
        </div>
    );
};

export default NuestroTiempo;
